import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, CheckCircle2, Clipboard, LoaderCircle, Mail } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TurnstileWidget } from '../../components/forms/TurnstileWidget';
import { siteConfig } from '../../config/site';
import { buildFallbackMessage, buildMailto } from './buildFallbackMessage';
import { submitProjectInquiry } from './projectInquiryApi';
import {
  projectInquiryDefaults,
  projectInquirySchema,
  type ProjectInquiryInput,
  type ProjectInquiryPayload,
  type ProjectInquiryValues,
  type ProjectType,
} from './projectInquirySchema';

const projectTypeOptions: Array<{ value: ProjectType; label: string }> = [
  { value: 'business-website', label: 'Business Website' },
  { value: 'corporate-website', label: 'Corporate Website' },
  { value: 'b2b-catalogue-rfq', label: 'B2B Catalogue / RFQ' },
  { value: 'website-redesign', label: 'Website Redesign' },
  { value: 'landing-page', label: 'Landing Page' },
  { value: 'not-sure', label: 'Not sure yet' },
];

function createRequestId() {
  return globalThis.crypto?.randomUUID?.() ?? 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = (Math.random() * 16) | 0;
    return (char === 'x' ? value : (value & 3) | 8).toString(16);
  });
}


function currentTimestamp() {
  return Date.now();
}

function FieldError({ message }: { message?: string }) {
  return message ? <span className="field-error">{message}</span> : null;
}

export function ProjectInquiryForm({
  initialProjectType,
  initialFreePreview = false,
  id,
}: {
  initialProjectType?: ProjectType;
  initialFreePreview?: boolean;
  id?: string;
}) {
  const [startedAt, setStartedAt] = useState(currentTimestamp);
  const [turnstile, setTurnstile] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [serverMessage, setServerMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [fallbackValues, setFallbackValues] = useState<ProjectInquiryValues | null>(null);
  const [abortController, setAbortController] = useState<AbortController>();

  const defaults: ProjectInquiryValues = {
    ...projectInquiryDefaults,
    project_type: initialProjectType ?? ('' as ProjectType),
    free_preview_requested: initialFreePreview,
  };

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<ProjectInquiryInput, unknown, ProjectInquiryValues>({
    resolver: zodResolver(projectInquirySchema),
    defaultValues: defaults,
  });

  const utm = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  }, []);

  const onSubmit = async (values: ProjectInquiryValues) => {
    setStatus('idle');
    setServerMessage('');
    setFallbackValues(null);
    abortController?.abort();
    const controller = new AbortController();
    setAbortController(controller);

    const payload: ProjectInquiryPayload = {
      ...values,
      request_id: createRequestId(),
      started_at: startedAt,
      page_url: window.location.href,
      referrer: document.referrer,
      ...utm,
      turnstile_token: turnstile || undefined,
    };

    try {
      await submitProjectInquiry(payload, controller.signal);
      setStatus('success');
      setServerMessage('Thank you — your inquiry has been received. I’ll review the details and reply by email with the next step.');
      reset(defaults);
      setStartedAt(currentTimestamp());
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setFallbackValues(values);
      setStatus('error');
      setServerMessage(error instanceof Error ? error.message : 'The request could not be sent. Please use the email fallback below.');
    }
  };

  const copy = async () => {
    if (!fallbackValues) return;
    await navigator.clipboard.writeText(buildFallbackMessage(fallbackValues));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const tokenCallback = useCallback((token: string) => setTurnstile(token), []);

  return (
    <form id={id} className="review-form project-inquiry-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="form-grid">
        <label><span>Your Name *</span><input autoComplete="name" {...register('name')} /><FieldError message={errors.name?.message} /></label>
        <label><span>Business / Company Name</span><input autoComplete="organization" {...register('business_name')} /><FieldError message={errors.business_name?.message} /></label>
        <label><span>Email Address *</span><input type="email" autoComplete="email" {...register('email')} /><FieldError message={errors.email?.message} /></label>
        <label><span>Country *</span><input autoComplete="country-name" {...register('country')} /><FieldError message={errors.country?.message} /></label>
        <label>
          <span>Project Type *</span>
          <select {...register('project_type')}>
            <option value="">Select project type…</option>
            {projectTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <FieldError message={errors.project_type?.message} />
        </label>
        <label><span>Current Website (optional)</span><input type="url" inputMode="url" {...register('current_website')} /><FieldError message={errors.current_website?.message} /></label>
        <label className="form-grid__full"><span>Project Details (optional)</span><textarea rows={6} {...register('project_details')} /><FieldError message={errors.project_details?.message} /></label>
      </div>

      <label className="form-checkbox">
        <input type="checkbox" {...register('free_preview_requested')} />
        <span>I'd like a free preview if my project is a good fit.</span>
      </label>

      <label className="honeypot" aria-hidden="true">Company website<input tabIndex={-1} autoComplete="off" {...register('company_website')} /></label>
      <TurnstileWidget onToken={tokenCallback} />

      <button className="form-submit" type="submit" disabled={isSubmitting}>
        {isSubmitting ? <><LoaderCircle className="spin" /> Sending inquiry…</> : <>Send Project Inquiry <span aria-hidden="true">↗</span></>}
      </button>
      <p className="form-micro">No commitment · Your information is only used to respond to this inquiry</p>
      <p className="form-policy">By submitting this form, you acknowledge the <a href="/privacy">Privacy Policy</a>.</p>

      <div className="form-status" aria-live="polite">
        {status === 'success' ? <div className="form-message form-message--success success-state"><CheckCircle2 /><p>{serverMessage}</p></div> : null}
        {status === 'error' && fallbackValues ? (
          <div className="form-message form-message--error"><AlertCircle /><div><p>{serverMessage}</p><div className="fallback-actions">
            <button type="button" onClick={copy}><Clipboard size={17} />{copied ? 'Copied' : 'Copy Request Details'}</button>
            <a href={buildMailto(siteConfig.email, fallbackValues)}><Mail size={17} />Send by Email</a>
          </div><small>Your form entries have been kept.</small></div></div>
        ) : null}
      </div>
    </form>
  );
}
